import { tryOnScopeDispose } from '@vueuse/core';
import type { UseEventBusReturn } from '@vueuse/core';

/**
 * SSR-safe replacement for `bus.on(listener)` on a @vueuse `useEventBus` bus.
 *
 * @vueuse keeps event-bus listeners in a module-global Map, and its automatic
 * cleanup never runs during SSR — the component scope is never disposed on the
 * server. So calling `.on()` during a server render leaks the listener closure
 * (which pins that render's whole scope: component state, fetched data, resolved
 * head tags) for the lifetime of the process. Under crawler traffic this grows
 * the heap until the Node process OOMs.
 *
 * Event buses only ever emit on the client (user interactions), so there is no
 * reason to register listeners on the server. This helper registers the
 * listener on the client only and is a drop-in replacement:
 *
 *   const off = bus.on(handler)  ->  const off = onClientEventBus(bus, handler)
 *
 * Cleanup: @vueuse's own auto-dispose is bound to the scope active when
 * `useEventBus(key)` *was created*, not when `.on()` runs. For a module-level
 * bus (e.g. `searchSuggestBus`) that scope is `undefined`, so `.on()` registers
 * no cleanup at all and the listener leaks/duplicates across client-side
 * navigation. We instead call `tryOnScopeDispose(off)` here, which binds to the
 * scope of the *consuming* component (this wrapper runs during its setup), so
 * the listener is removed on unmount regardless of where the bus was created.
 * For an in-scope bus this is a harmless second, idempotent `off`. The returned
 * unsubscribe is still available for manual cleanup and is a no-op on the
 * server, so existing wiring (onUnmounted, cleanup arrays) is unchanged.
 */

// Listener typed as a method so its parameter is checked bivariantly, matching
// how `useEventBus().on()` (a method) accepts listeners. A plain function-typed
// parameter would be contravariant under strictFunctionTypes and reject narrowly
// typed handlers on buses created without an explicit generic (T = unknown).
type EventBusListenerArg<T, P> = {
	listener(event: T, payload?: P): void;
}['listener'];

// `NoInfer` on the listener keeps T/P inferred solely from `bus`, so the
// listener is type-checked against the bus's element type (exactly like
// `bus.on(listener)`) instead of widening/conflicting T from the handler.
export function onClientEventBus<T, P = any>(
	bus: UseEventBusReturn<T, P>,
	listener: EventBusListenerArg<NoInfer<T>, NoInfer<P>>
): () => void {
	if (import.meta.client) {
		const off = bus.on(listener);
		tryOnScopeDispose(off);
		return off;
	}
	return () => {};
}
